import { useState, useEffect } from 'react';
import { api } from '@/lib/api/client';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { BottomNav } from '@/components/BottomNav';
import { ChevronLeft, ChevronRight, User, CreditCard, Brain, Cpu, Archive, Shield, Bell, Palette, Lock, BarChart3, Settings2, LogOut, Keyboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { firebaseAuthService } from "@/../frontend/services/firebaseAuth.service";
import { useToast } from "@/hooks/use-toast";

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Observer toggle (server-backed)
  const [observerMode, setObserverMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await api.get('/settings');
        setObserverMode(!!settings?.observer);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleObserverModeChange = async (checked: boolean) => {
    setObserverMode(checked);
    try {
      await api.post('/settings', { observer: checked });
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  // Local demo toggles (UI only)
  const [toggleStates, setToggleStates] = useState({
    notifications: true,
    interface: true,
    safety: true,
    computeBudget: true,
    limits: true,
  });

  const handleToggle = (key: string) => {
    setToggleStates(prev => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev]
    }));
  };

  const handleSignOut = async () => {
    try {
      await firebaseAuthService.signOut();
      toast({ title: "Signed out", description: "You have been successfully signed out." });
      // App-level auth guard should redirect
    } catch (error) {
      console.error('Sign out error:', error);
      toast({ title: "Sign out failed", description: "Please try again.", variant: "destructive" });
    }
  };

  const settingsItems = [
    { icon: User, label: "Profile", hasArrow: true, onClick: () => navigate("/settings/profile") },
    { icon: CreditCard, label: "Subscription", hasArrow: true, onClick: () => navigate("/settings/subscription") },
    { icon: Keyboard, label: "BC Keyboard", subtitle: "Type with your council, anywhere", hasArrow: true, onClick: () => navigate("/settings/keyboard") },
    { icon: Brain, label: "Colonies", subtitle: "3 agents", hasArrow: true, onClick: () => navigate("/settings/colonies") },
    { icon: Cpu, label: "Models & Providers", hasArrow: true, onClick: () => navigate("/settings/models") },
    { icon: Archive, label: "Memory", hasArrow: true, onClick: () => navigate("/settings/memory") },
    { icon: Shield, label: "Privacy & Sharing", hasArrow: true, onClick: () => navigate("/settings/privacy") },
    { icon: Bell, label: "Notifications", hasSwitch: true, key: "notifications", enabled: toggleStates.notifications },
    { icon: Palette, label: "Interface", hasSwitch: true, key: "interface", enabled: toggleStates.interface },
    { icon: Lock, label: "Safety", hasSwitch: true, key: "safety", enabled: toggleStates.safety },
    { icon: BarChart3, label: "Compute Budget", hasSwitch: true, key: "computeBudget", enabled: toggleStates.computeBudget },
    { icon: Settings2, label: "Limits", hasSwitch: true, key: "limits", enabled: toggleStates.limits },
    { icon: LogOut, label: "Sign Out", hasArrow: false, isSignOut: true, onClick: handleSignOut },
  ];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-ios-separator bg-ios-surface">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate("/")}
          className="text-foreground hover:bg-ios-surface-secondary w-11 h-11 rounded-full"
        >
          <ChevronLeft size={22} />
        </Button>
        <h1 className="ios-title text-foreground">Settings</h1>
        <div className="w-11" />
      </div>

      {/* Observer toggle (server-backed) */}
      <div className="p-4 border-b border-ios-separator bg-ios-surface">
        <div className="flex items-center justify-between">
          <Label htmlFor="observer-mode">Alt-me observes (silent)</Label>
          <Switch id="observer-mode" checked={observerMode} onCheckedChange={handleObserverModeChange} />
        </div>
      </div>

      {/* Settings List */}
      <div className="flex-1 bg-ios-surface">
        <div className="divide-y divide-ios-separator">
          {settingsItems.map((item, itemIndex) => {
            const IconComponent = item.icon as any;
            return (
              <div
                key={itemIndex}
                className={`ios-list-item flex items-center justify-between px-4 py-3 cursor-pointer ${
                  (item as any).isSignOut ? 'hover:bg-red-50 dark:hover:bg-red-900/20' : ''
                }`}
                onClick={item.onClick}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <div className="flex-shrink-0 mr-3">
                    <IconComponent size={24} className={(item as any).isSignOut ? "text-red-500" : "text-ios-blue"} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium ${ (item as any).isSignOut ? "text-red-500" : "text-foreground" }`}>
                      {item.label}
                    </div>
                    {(item as any).subtitle && (
                      <div className="text-muted-foreground text-sm">{(item as any).subtitle}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  {item.hasArrow && (
                    <ChevronRight size={18} className="text-muted-foreground" />
                  )}
                  {item.hasSwitch && (item as any).key && (
                    <div 
                      className={`ios-toggle ${(item as any).enabled ? 'enabled' : ''}`}
                      onClick={(e) => { e.stopPropagation(); handleToggle((item as any).key!); }}
                    >
                      <div className="ios-toggle-thumb" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <BottomNav currentPage="settings" />
    </div>
  );
};

export default Settings;
